/**

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
/* copied from Backstage repo plugins\catalog\src\components\AboutCard\AboutContent.tsx and customized */

import {
  Entity,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import {
  EntityRefLinks,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { Grid, makeStyles } from '@material-ui/core';
import { MarkdownContent } from '@backstage/core-components';

import { AboutField } from '@backstage/plugin-catalog';

const useStyles = makeStyles({
  description: {
    wordBreak: 'break-word',
  },
});

/**
 * Props for {@link AboutContent}.
 *
 * @public
 */
export interface AboutContentProps {
  entity: Entity;
}

/** @public */
export function AboutContent(props: AboutContentProps) {
  const { entity } = props;
  const classes = useStyles();

  // compute a normalized kind once and use a small helper to test membership
  const normalizedKind = String(entity?.kind ?? '').toLowerCase();
  const kindIs = (...candidates: string[]) =>
    candidates.some(k => normalizedKind === k);

  const partOfComponentRelations = getEntityRelations(
    entity,
    RELATION_PART_OF,
    { kind: 'component' },
  );
  const partOfDomainRelations = getEntityRelations(
    entity,
    RELATION_PART_OF,
    { kind: 'domain' },
  );

  return (
    <Grid container>
      <AboutField label="Description" gridSizes={{ xs: 12 }}>
        <MarkdownContent
          className={classes.description}
          content={entity?.metadata?.description ?? 'No description'}
        />
      </AboutField>

      {/* Domain: show for systems or when domain relations exist */}
      {(kindIs('system') || partOfDomainRelations.length > 0) && (
        <AboutField
          label="Domain"
          value="No Domain"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          {partOfDomainRelations.length > 0 && (
            <EntityRefLinks
              entityRefs={partOfDomainRelations}
              defaultKind="domain"
            />
          )}
        </AboutField>
      )}

      {/* Parent component: only for components with parent relations */}
      {kindIs('component') && partOfComponentRelations.length > 0 && (
        <AboutField
          label="Parent Component"
          value="No Parent Component"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          <EntityRefLinks
            entityRefs={partOfComponentRelations}
            defaultKind="component"
          />
        </AboutField>
      )}

      {/* Type field for a set of kinds or when spec.type exists */}
      {(
        kindIs('api', 'component', 'resource', 'template', 'group', 'location') ||
        typeof entity?.spec?.type === 'string'
      ) && (
        <AboutField
          label="Type"
          value={entity?.spec?.type as string}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        />
      )}
    </Grid>
  );
}
